import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Funding,
  FundingCloseRequest,
  FundingCreateRequest,
  FundingLaunchRequest,
  FundingReward,
  FundingRewardCreateRequest,
  FundingRewardUpdateRequest,
  FundingUpdateRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { fundingCampaignQueryKey } from './useFundingCampaignQuery';
import { fundingStatisticsQueryKey } from './useFundingStatisticsQuery';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number, op: string): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, {
      status,
      ...(env.meta ? { meta: env.meta } : {}),
    });
  }
  throw new TransportError(`Funding ${op} failed with status ${status}`, raw);
};

const invalidateCampaign = async (
  qc: ReturnType<typeof useQueryClient>,
  campaignId?: string,
) => {
  if (campaignId) {
    await qc.invalidateQueries({ queryKey: fundingCampaignQueryKey(campaignId) });
    await qc.invalidateQueries({ queryKey: fundingStatisticsQueryKey(campaignId) });
    await qc.invalidateQueries({ queryKey: ['admin', 'funding-pledges'] });
    await qc.invalidateQueries({ queryKey: ['admin', 'funding-timeline', campaignId] });
  }
  await qc.invalidateQueries({ queryKey: ['admin', 'funding-campaigns'] });
};

export const useCreateFundingCampaignMutation = () => {
  const qc = useQueryClient();
  return useMutation<Funding, Error, FundingCreateRequest>({
    mutationFn: async (body) => {
      const path = '/admin/v1/funding-campaigns' as const;
      const idempotencyKey = idempotencyKeyFor('POST', path, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: { header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'create');
      return (data as { data: Funding }).data;
    },
    onSuccess: (funding) => invalidateCampaign(qc, funding.campaignId),
  });
};

export interface UpdateFundingInput {
  campaignId: string;
  body: FundingUpdateRequest;
}

export const useUpdateFundingCampaignMutation = () => {
  const qc = useQueryClient();
  return useMutation<Funding, Error, UpdateFundingInput>({
    mutationFn: async ({ campaignId, body }) => {
      const path = '/admin/v1/funding-campaigns/{campaignId}' as const;
      const concrete = path.replace('{campaignId}', campaignId);
      const idempotencyKey = idempotencyKeyFor('PATCH', concrete, body);
      const { data, error, response } = await apiClient.raw.PATCH(path, {
        body,
        params: {
          path: { campaignId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'update');
      return (data as { data: Funding }).data;
    },
    onSuccess: (_funding, { campaignId }) => invalidateCampaign(qc, campaignId),
  });
};

export const useDeleteFundingCampaignMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (campaignId) => {
      const path = '/admin/v1/funding-campaigns/{campaignId}' as const;
      const concrete = path.replace('{campaignId}', campaignId);
      const idempotencyKey = idempotencyKeyFor('DELETE', concrete, '');
      const { error, response } = await apiClient.raw.DELETE(path, {
        params: {
          path: { campaignId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (response.status >= 400) throwIfError(error, response.status, 'delete');
    },
    onSuccess: (_void, campaignId) => invalidateCampaign(qc, campaignId),
  });
};

export interface LaunchFundingInput {
  campaignId: string;
  body?: FundingLaunchRequest | undefined;
}

export const useLaunchFundingCampaignMutation = () => {
  const qc = useQueryClient();
  return useMutation<Funding, Error, LaunchFundingInput>({
    mutationFn: async ({ campaignId, body }) => {
      const path = '/admin/v1/funding-campaigns/{campaignId}/launch' as const;
      const concrete = path.replace('{campaignId}', campaignId);
      const idempotencyKey = idempotencyKeyFor('POST', concrete, body ?? {});
      const { data, error, response } = await apiClient.raw.POST(path, {
        body: body ?? {},
        params: {
          path: { campaignId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'launch');
      return (data as { data: Funding }).data;
    },
    onSuccess: (_f, { campaignId }) => invalidateCampaign(qc, campaignId),
  });
};

export interface CloseFundingInput {
  campaignId: string;
  body: FundingCloseRequest;
}

export const useCloseFundingCampaignMutation = () => {
  const qc = useQueryClient();
  return useMutation<Funding, Error, CloseFundingInput>({
    mutationFn: async ({ campaignId, body }) => {
      const path = '/admin/v1/funding-campaigns/{campaignId}/close' as const;
      const concrete = path.replace('{campaignId}', campaignId);
      const idempotencyKey = idempotencyKeyFor('POST', concrete, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: {
          path: { campaignId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'close');
      return (data as { data: Funding }).data;
    },
    onSuccess: (_f, { campaignId }) => invalidateCampaign(qc, campaignId),
  });
};

export interface CreateRewardInput {
  campaignId: string;
  body: FundingRewardCreateRequest;
}

export const useCreateFundingRewardMutation = () => {
  const qc = useQueryClient();
  return useMutation<FundingReward, Error, CreateRewardInput>({
    mutationFn: async ({ campaignId, body }) => {
      const path = '/admin/v1/funding-campaigns/{campaignId}/rewards' as const;
      const concrete = path.replace('{campaignId}', campaignId);
      const idempotencyKey = idempotencyKeyFor('POST', concrete, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: {
          path: { campaignId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'reward-create');
      return (data as { data: FundingReward }).data;
    },
    onSuccess: (_r, { campaignId }) => invalidateCampaign(qc, campaignId),
  });
};

export interface UpdateRewardInput {
  campaignId: string;
  rewardId: string;
  body: FundingRewardUpdateRequest;
}

export const useUpdateFundingRewardMutation = () => {
  const qc = useQueryClient();
  return useMutation<FundingReward, Error, UpdateRewardInput>({
    mutationFn: async ({ campaignId, rewardId, body }) => {
      const path =
        '/admin/v1/funding-campaigns/{campaignId}/rewards/{rewardId}' as const;
      const concrete = path
        .replace('{campaignId}', campaignId)
        .replace('{rewardId}', rewardId);
      const idempotencyKey = idempotencyKeyFor('PATCH', concrete, body);
      const { data, error, response } = await apiClient.raw.PATCH(path, {
        body,
        params: {
          path: { campaignId, rewardId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'reward-update');
      return (data as { data: FundingReward }).data;
    },
    onSuccess: (_r, { campaignId }) => invalidateCampaign(qc, campaignId),
  });
};

export interface DeleteRewardInput {
  campaignId: string;
  rewardId: string;
}

export const useDeleteFundingRewardMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteRewardInput>({
    mutationFn: async ({ campaignId, rewardId }) => {
      const path =
        '/admin/v1/funding-campaigns/{campaignId}/rewards/{rewardId}' as const;
      const concrete = path
        .replace('{campaignId}', campaignId)
        .replace('{rewardId}', rewardId);
      const idempotencyKey = idempotencyKeyFor('DELETE', concrete, '');
      const { error, response } = await apiClient.raw.DELETE(path, {
        params: {
          path: { campaignId, rewardId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (response.status >= 400)
        throwIfError(error, response.status, 'reward-delete');
    },
    onSuccess: (_void, { campaignId }) => invalidateCampaign(qc, campaignId),
  });
};
